import type { IJourneyStep } from '@pushly/models/lib/structs/journeys'
import type { IRecurrenceSchedule } from '@pushly/models/lib/interfaces/recurrence-schedule'
import type { IJourneySubscriberFcapRequest } from '@pushly/models/lib/structs/journeys/requests/request-interfaces'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { WithOptional } from '@pushly/aqe/lib/types'
import { ExitBehavior, ExitStatus, JourneyType, TriggerType } from '@pushly/models/lib/enums/journeys'
import * as moment from 'moment-timezone'
import { StatusType } from '../../../enums/status-type'

// ──────────────────────────────────────────────────────────────────────
// TODO: Align with @pushly/models journey response types
// ──────────────────────────────────────────────────────────────────────
//
// The types below (Journey, JourneyConfiguration, JourneyRevision, etc.)
// duplicate fields from the js-models response interfaces:
//   - JourneyResponse          (@pushly/models/lib/structs/journeys/responses)
//   - JourneyConfigurationResponse
//   - JourneyRevisionResponse
//   - JourneyStepResponse
//
// Those js-models types are currently plain interfaces. The notification
// domain uses full AbstractStruct classes (e.g. GetNotificationResponse)
// with @Expose(), @Type(), validators, and identity guards. Journey
// responses should follow the same pattern.
//
// Migration plan:
//   1. Convert js-models journey response interfaces → AbstractStruct classes
//      (add @Expose, @Type for nested objects, validators, identity guards)
//   2. Align field differences between pufferfish's local types and js-models:
//      - Journey.steps (flattened from revision — pufferfish-only convenience field)
//      - Journey.campaign_type (legacy alias of `type`)
//      - Journey.date_start_utc / date_end_utc (UTC variants — pufferfish-only)
//      - Journey.entrances/exits/deliveries/impressions/clicks/ctr/delivery_rate (stats)
//      - JourneyConfiguration.audience is required here with expanded segment
//        objects, but optional in JourneyConfigurationResponse with only IDs
//   3. Replace local types with: type Journey = JourneyResponse & { ...pufferfish-only fields }
//   4. Update all 39 importing files to use the shared types
//
// Blocked on: audience shape alignment (required vs optional, expanded
// segment objects vs ID arrays) and stats fields that only exist in
// list-view API responses.
//
// Impact: ~39 files, ~200 type annotation sites in pufferfish.
// Risk: moderate — shape mismatches at runtime need manual testing.
// Recommendation: separate PR after the journeys refactor lands.
// ──────────────────────────────────────────────────────────────────────

// region Journey API Response

// region generic user based properties

export type UserCreatedItem = {
    created_by?: number
    created_at: string | Date | null // ISO date time string
    created_by_user_name?: string
    destroyed_at?: string | null
}

export type UserUpdatedItem = {
    updated_by?: number | null
    updated_at?: string | null // ISO date time string
    updated_by_user_name?: string
}

// endregion

export type JourneyRevision = {
    id: number
    is_active: boolean
    created_at: string // ISO date time string
    steps: IJourneyStep[]
}

export type JourneyAudience = {
    segment_ids?: number[]
    excluded_segment_ids?: number[]
    default_icon_url?: string
    evaluation_frequency?: string
    segments?: Array<{
        id: number
        name: string
        is_default: boolean
        segment_type: string
    }>
    excluded_segments?: Array<{
        id: number
        name: string
        is_default: boolean
        segment_type: string
    }>
}

/** Item group targeting as returned by the API (raw DB shape). */
export type JourneyItemGroupTargeting = {
    item_group_ids?: number[]
    excluded_item_group_ids?: number[]
}

export type JourneyConfiguration = {
    audience: JourneyAudience
    end_status: ExitStatus
    end_behavior: ExitBehavior
    abandonment_seconds?: number
    abandonment_metric?: string
    abandoned_cart_cooldown_period?: { interval_seconds: number; display_metric?: string }
    abandoned_browse_minimum_item_views?: number
    item_group_targeting?: JourneyItemGroupTargeting
    computed_trigger_type?: TriggerType
    schedule?: IRecurrenceSchedule
    subscriber_fcap?: IJourneySubscriberFcapRequest
    quiet_hours?: { start: string; end: string }
}

export type Journey = {
    id: number
    domain_id: number
    /** Canonical journey type. Alias of `campaign_type` from the API. */
    type: JourneyType
    /** Legacy API field — prefer `type`. Present when the API returns the raw column name. */
    campaign_type?: JourneyType
    name: string
    /** Numeric status ID — compare against StatusType.*.id */
    status_id: number
    /** Status name string — compare against StatusType.*.name (e.g. 'DRAFT', 'ACTIVE') */
    status: string
    revision: JourneyRevision
    revisions: JourneyRevision[]
    date_time_zone: string
    date_start: string | Date
    date_start_utc?: string | Date
    date_end: string | Date | null
    date_end_utc?: string | Date | null
    date_completed_utc: string | Date | null
    manually_completed: boolean | null
    configuration: JourneyConfiguration
    channels: DeliveryChannel[]
    /** Soft-visibility flag — when true the journey is hidden from default list views. */
    is_hidden: boolean
    steps: IJourneyStep[]
    /** Table-level stats fields (present when fetched for list views) */
    entrances?: number
    exits?: number
    deliveries?: number
    impressions?: number
    clicks?: number
    ctr?: number
    delivery_rate?: number
} & UserCreatedItem &
    WithOptional<UserUpdatedItem, 'updated_at' | 'updated_by'>

// endregion

// region Journey Helper Functions

/** Check if a journey is completed based on status and dates. */
export function isJourneyCompleted(journey: Pick<Journey, 'status_id' | 'date_completed_utc' | 'manually_completed' | 'date_end' | 'date_end_utc'>): boolean {
    let runDatesCompleted = false

    if (journey.date_completed_utc || journey.manually_completed) {
        runDatesCompleted = true
    } else if (journey.date_end) {
        const now = moment.utc()
        const dateEnd = moment.tz(journey.date_end_utc || journey.date_end, 'utc')
        runDatesCompleted = dateEnd <= now
    }

    return (
        runDatesCompleted ||
        journey.status_id === StatusType.COMPLETED.id ||
        journey.status_id === StatusType.CANCELLED.id
    )
}

/** Resolved end behavior from journey configuration. */
export interface JourneyEndBehavior {
    behavior: ExitBehavior
    status?: ExitStatus
}

/** Resolve the end behavior and status from a journey's configuration. */
export function getJourneyEndBehavior(journey: Pick<Journey, 'configuration'>): JourneyEndBehavior {
    const config = journey.configuration
    const behavior = config?.end_behavior || ExitBehavior.BLEED
    if (behavior === ExitBehavior.STOP) {
        return { behavior, status: config?.end_status || ExitStatus.COMPLETED }
    }
    return { behavior }
}

/** Encode a JourneyEndBehavior into a composite select value (e.g. 'BLEED', 'STOP:completed'). */
export function encodeEndBehaviorValue(eb: JourneyEndBehavior): string {
    if (eb.behavior === ExitBehavior.STOP && eb.status) {
        return `${eb.behavior}:${eb.status}`
    }
    return eb.behavior
}

/** Decode a composite select value back into a JourneyEndBehavior. */
export function decodeEndBehaviorValue(value: string): JourneyEndBehavior {
    const parts = value.split(':')
    const behavior = parts[0] as ExitBehavior
    if (behavior === ExitBehavior.STOP && parts.length === 2) {
        return { behavior, status: parts[1] as ExitStatus }
    }
    return { behavior }
}

// endregion
